import React, { useEffect, useRef, useState, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Swal from 'sweetalert2';
import MyContext from '../../../MyContext';
import './vendor.css';

const CreatePurchase = ({ userID }) => {
  const { baseUrl } = useContext(MyContext);
  const BASIC_AUTH = "YW5sb29rQGdtYWlsLmNvbTpBbmxvb2tAMTIzNDU=";
  const navigate = useNavigate();
  const { state } = useLocation();
  const vendor = state?.vendor;

  const vendorId = vendor?.vendorId || vendor?.id;
  const storageKey = `purchase-items-${userID}-${vendorId}`;

  const itemIdRef = useRef(null);
  const [itemId, setItemId] = useState('');
  const [items, setItems] = useState([]);

  useEffect(() => {
    if (!vendorId) return;
    const saved = localStorage.getItem(storageKey);
    if (saved) {
      try {
        setItems(JSON.parse(saved));
      } catch {
        setItems([]);
      }
    }
  }, [userID, vendorId]);

  useEffect(() => {
    if (vendorId) {
      localStorage.setItem(storageKey, JSON.stringify(items));
    }
  }, [items, vendorId]);

  useEffect(() => {
    itemIdRef.current?.focus();
  }, []);

  const fetchItemDetails = async () => {
    if (!itemId) return;
    if (items.some(i => i.item_id === itemId)) {
      Swal.fire('Already Added', 'This item is already in the list', 'info');
      setItemId('');
      return;
    }

    try {
      const res = await fetch(`${baseUrl}/api/Inventory/v1.0/getInventoryItemDetails`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Basic ${BASIC_AUTH}`,
        },
        body: JSON.stringify({ userid: userID, itemId }),
      });
      const data = await res.json();
      if (data?.responseCode === '00') {
        const newItem = {
          ...data.response,
          quantity: 1,
          purchasePrice: data.response.price,
        };
        setItems(prev => [...prev, newItem]);
        setItemId('');
      } else {
        Swal.fire('Not Found', 'Item not found', 'warning');
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleItemChange = (index, field, value) => {
    const updated = [...items];
    updated[index][field] = value;
    setItems(updated);
  };

  const handleRemoveItem = (index) => {
    const updated = [...items];
    updated.splice(index, 1);
    setItems(updated);
  };

  const handleCreatePurchase = async () => {
    if (items.length === 0) {
      Swal.fire('No items', 'Please add at least one item', 'warning');
      return;
    }

    const payload = {
      userid: String(userID),
      vendorId: String(vendorId),
      items: items.map(i => ({
        itemId: String(i.item_id),
        quantity: String(i.quantity),
        purchasePrice: String(i.purchasePrice),
      })),
    };

    const confirm = await Swal.fire({
      title: 'Confirm Purchase?',
      text: `You are about to submit ${items.length} item(s).`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Yes, submit!',
    });

    if (confirm.isConfirmed) {
      try {
        const res = await fetch(`${baseUrl}/api/Inventory/v1.0/createPurchaseOrder`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Basic ${BASIC_AUTH}`,
          },
          body: JSON.stringify(payload),
        });
        const result = await res.json();
        if (result.responseCode === '00') {
          Swal.fire('Success', 'Purchase created successfully', 'success').then(() => {
            localStorage.removeItem(storageKey);
            navigate('/Inventory/Vendors');
          });
        } else {
          Swal.fire('Failed', result.responseMessage || 'Something went wrong', 'error');
        }
      } catch (err) {
        console.error(err);
        Swal.fire('Error', 'API error', 'error');
      }
    }
  };

  const handleRemoveAll = () => {
    Swal.fire({
      title: 'Clear All Items?',
      text: 'This will remove all items from the list.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Clear All',
    }).then(result => {
      if (result.isConfirmed) {
        setItems([]);
      }
    });
  };

  return (
    <div className="vendor-create-page-wrapper">
      <div className="vendor-form-header">
        <h2>Create Purchase from {vendor?.vendorName}</h2>
        <div className="d-flex gap-2">
          <button className="vendor-btn-cancel" onClick={() => {
            localStorage.removeItem(storageKey);
            navigate('/Inventory/Vendors');
          }}>Back</button>
          <button className="vendor-btn-save" onClick={handleCreatePurchase}>+ Create Purchase</button>
          <button className="vendor-btn-refresh" onClick={handleRemoveAll}>Remove All</button>
        </div>
      </div>

      <input
        ref={itemIdRef}
        type="text"
        placeholder="Enter Barcode or Item ID"
        value={itemId}
        className="vendor-form-control"
        onChange={(e) => setItemId(e.target.value)}
        onKeyDown={(e) => e.key === 'Enter' && fetchItemDetails()}
      />

      <div className="added-items-list">
        {items.map((item, index) => (
          <div key={item.item_id} className="item-preview-box" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', borderBottom: '1px solid #e5e7eb' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
              <img src={item.photo} alt={item.item_name} style={{ width: '36px', height: '36px', borderRadius: '4px', objectFit: 'cover' }} />
              <div style={{ flex: 1 }}>
                <strong>{item.item_name}</strong>
                <div style={{ fontSize: '13px', color: '#6b7280' }}>{item.size}</div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <button className="qty-btn" onClick={() => handleItemChange(index, 'quantity', Math.max(1, parseInt(item.quantity) - 1))}>−</button>
                <input
                  type="number"
                  className="vendor-form-control qty-input"
                  value={item.quantity}
                  onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                />
                <button className="qty-btn" onClick={() => handleItemChange(index, 'quantity', parseInt(item.quantity) + 1)}>+</button>
              </div>
              <input
                type="number"
                className="vendor-form-control qty-input"
                placeholder="Price"
                value={item.purchasePrice}
                onChange={(e) => handleItemChange(index, 'purchasePrice', e.target.value)}
              />
            </div>
            <button className="vendor-btn-delete" style={{ marginLeft: '12px' }} onClick={() => handleRemoveItem(index)}>Remove</button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default CreatePurchase;
